import {
  type QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useEffect, useState, type JSX } from 'react';

import { AppDialog } from '@/components/ui/app-dialog.js';
import { Button } from '@/components/ui/button.js';
import { Skeleton } from '@/components/ui/skeleton.js';
import {
  ApiError,
  activateSkillDraft,
  discardSkillDraft,
  fetchSkillDraft,
  skillDraftKey,
  type SkillDraft,
} from '@/lib/api/skills.js';
import { notify } from '@/lib/notify.js';

import { SkillContentPreview } from './SkillContentPreview.js';

/** Shown when saving a draft finds the skill changed since the draft began, by anyone, the caller included. */
export const DRAFT_CONFLICT_MESSAGE =
  'This skill changed since your unsaved changes were made. Ask the agent to combine your changes with the newer version.';

/** Shown while the draft overlaps a newer version and cannot be saved until the agent settles it. */
export const DRAFT_OVERLAP_MESSAGE =
  'Your changes overlap with a newer version. Ask the agent which version to keep before saving.';

/** Refreshes the skill lists but not the draft queries, so a gone draft is not fetched again. */
function refreshSkillLists(
  queryClient: QueryClient,
  skillId: string,
): Promise<void> {
  return queryClient.invalidateQueries({
    queryKey: ['skills'],
    predicate: (query) => query.queryKey[1] !== skillDraftKey(skillId)[1],
  });
}

/**
 * The note telling the caller their chat edits wait unsaved. They can read the draft, save it as the
 * new version, or discard it after a confirmation.
 */
export function SkillDraftNote({ skillId }: { skillId: string }): JSX.Element {
  const [reviewing, setReviewing] = useState(false);
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);
  const queryClient = useQueryClient();
  const refreshSkills = () => refreshSkillLists(queryClient, skillId);
  const draft = useQuery({
    queryKey: skillDraftKey(skillId),
    queryFn: () => fetchSkillDraft(skillId),
    retry: false,
  });
  const draftGone =
    draft.error instanceof ApiError && draft.error.status === 404;
  useEffect(() => {
    if (draftGone) void refreshSkillLists(queryClient, skillId);
  }, [draftGone, queryClient, skillId]);
  const overlapping = (draft.data?.conflicts?.length ?? 0) > 0;
  const save = useMutation({
    mutationFn: () => activateSkillDraft(skillId),
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: skillDraftKey(skillId) });
      void refreshSkills();
      notify.success('Changes saved');
    },
    onError: (error) => {
      if (error instanceof ApiError && error.status === 409) {
        notify.error(
          error.conflicts.length > 0
            ? DRAFT_OVERLAP_MESSAGE
            : DRAFT_CONFLICT_MESSAGE,
        );
        void refreshSkills();
        void queryClient.invalidateQueries({
          queryKey: skillDraftKey(skillId),
        });
      }
    },
  });
  const discard = useMutation({
    mutationFn: () => discardSkillDraft(skillId),
    onSuccess: () => {
      setConfirmingDiscard(false);
      queryClient.removeQueries({ queryKey: skillDraftKey(skillId) });
      void refreshSkills();
      notify.success('Unsaved changes discarded');
    },
    onError: () => setConfirmingDiscard(false),
  });
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-border/40 bg-muted/20 px-3 py-2 text-xs text-foreground">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span>You have unsaved changes from chat</span>
        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="sm"
            aria-expanded={reviewing}
            onClick={() => setReviewing((wasReviewing) => !wasReviewing)}
          >
            Review
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={save.isPending || !draft.data || overlapping}
            onClick={() => save.mutate()}
          >
            Save
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setConfirmingDiscard(true)}
          >
            Discard
          </Button>
        </div>
      </div>
      {overlapping && (
        <p className="text-muted-foreground">{DRAFT_OVERLAP_MESSAGE}</p>
      )}
      {reviewing && <DraftPreview draft={draft} />}
      <AppDialog
        open={confirmingDiscard}
        onOpenChange={setConfirmingDiscard}
        title="Discard your unsaved changes? This cannot be undone."
      >
        <div className="mt-3 flex justify-end gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setConfirmingDiscard(false)}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={discard.isPending}
            onClick={() => discard.mutate()}
          >
            Discard
          </Button>
        </div>
      </AppDialog>
    </div>
  );
}

/** The draft's instructions and file paths, read only. */
function DraftPreview({
  draft,
}: {
  draft: UseQueryResult<SkillDraft>;
}): JSX.Element {
  if (draft.isError) {
    return <p className="text-muted-foreground">Could not load the draft.</p>;
  }
  if (!draft.data) return <Skeleton className="h-12 w-full rounded-lg" />;
  return (
    <SkillContentPreview
      instructions={draft.data.instructions}
      resources={draft.data.resources}
    />
  );
}
